/**
 * Who a gift is for: search the people we have, or type a new person with a
 * postal address. Shared by Record a payment and the Mail Order Batch; the
 * state and the duplicate gate are in recipient.ts.
 */
import type { MatchCandidate } from './api'
import type { CountryChoice } from './payment'
import { BLANK_ADDRESS, blankRcpt, type Address, type Recipient, type RecipientGate } from './recipient'
import { personKey } from './payment'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const inp = `min-h-[44px] w-full rounded-lg border border-ink-400 bg-ink-800 px-3 text-[15px] text-paper ${focus}`
const lbl = 'flex flex-col gap-1.5 text-[13px] font-semibold text-paper/70'
const btn = `inline-flex min-h-[44px] items-center justify-center rounded-lg border border-ink-400 px-4 text-[14px] font-bold text-paper hover:bg-ink-600 ${focus}`
const btnPrimary = `inline-flex min-h-[46px] items-center justify-center rounded-lg bg-paper px-5 font-bold text-ink-900 disabled:cursor-not-allowed disabled:opacity-45 ${focus}`
const grid = 'grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-3.5'

export function Candidates({ list, onUse, onDifferent }: { list: MatchCandidate[]; onUse: (c: MatchCandidate) => void; onDifferent: () => void }) {
  return (
    <div role="alert" className="flex flex-col gap-3 rounded-xl border border-amber-glow/40 bg-amber-wash p-4">
      <strong>This may be someone we already have</strong>
      {list.map((c) => (
        <div key={c.subscriber_id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-ink-700 p-3">
          <span className="flex flex-col gap-0.5">
            <strong>{c.name} <span className="font-mono font-normal text-paper/60">{c.account_number ? `#${c.account_number}` : ''}</span></strong>
            <span className="text-[13px] text-paper/70">{c.zipcode ?? ''}</span>
          </span>
          <button type="button" className={btnPrimary} onClick={() => onUse(c)}>Use this record</button>
        </div>
      ))}
      <button type="button" className={`${btn} self-start`} onClick={onDifferent}>No, this is a different person</button>
    </div>
  )
}

type TextKey = Exclude<keyof Address, 'country'>
export function AddressFields({ a, set }: { a: Address; set: (a: Address) => void }) {
  const f = (k: TextKey, label: string, extra = '') => (
    <label className={`${lbl} ${extra}`}>{label}
      <input className={inp} value={a[k]} type={k === 'email' ? 'email' : 'text'} onChange={(e) => set({ ...a, [k]: e.target.value })} />
    </label>
  )
  return (
    <div className={grid}>
      {f('company', 'Company (optional)', 'col-span-2')}
      {f('address_1', 'Street', 'col-span-2')}
      {f('address_2', 'Apartment or suite (optional)')}
      {f('zipcode', a.country === 'US' ? 'ZIP' : 'Postal code')}
      {f('city', 'City')}
      {f('state', a.country === 'US' ? 'State' : 'State or province')}
      <label className={lbl}>Country
        <select className={inp} value={a.country} onChange={(e) => set({ ...a, country: e.target.value as CountryChoice })}>
          <option value="US">United States</option>
          <option value="CA">Canada</option>
          <option value="OTHER">Other</option>
        </select>
      </label>
      {a.country === 'OTHER' && f('country_other', 'Which country')}
      {f('email', 'Email (optional)')}
    </div>
  )
}

/** One gift line's recipient. `gate` comes from useRecipientGates for this line. */
export function RecipientPicker({ r, set, gate }: { r: Recipient; set: (r: Recipient) => void; gate: RecipientGate }) {
  if (r.mode === 'existing') {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-lg bg-ink-600 p-3.5">
        <span>Gift for <strong>{r.label}</strong></span>
        <button type="button" className={btn} onClick={() => set(blankRcpt())}>Change</button>
      </div>
    )
  }
  const pending = gate.blocked && !gate.asking
  return (
    <div className="flex flex-col gap-3.5 rounded-lg bg-ink-600 p-3.5">
      <label className={lbl}>Recipient
        <input className={inp} value={r.name} placeholder="Search a name, or type a new person"
          onChange={(e) => set({ ...r, name: e.target.value, address: { ...BLANK_ADDRESS } })} />
      </label>
      {!gate.searching && <span className="text-[13px] text-paper/70">Type a first and last name to search.</span>}
      {gate.searching && pending && <span className="text-[13px] text-paper/70">Checking for people we already have…</span>}
      {gate.asking && (
        <Candidates list={gate.found}
          onUse={(c) => set({ ...r, mode: 'existing', id: c.subscriber_id, label: `${c.name}${c.account_number ? ` #${c.account_number}` : ''}` })}
          onDifferent={() => set({ ...r, dismissedKey: personKey(r.name) })} />
      )}
      {gate.searching && !pending && !gate.asking && (
        <>
          <span className="text-[13px] text-paper/70">New person. Add their mailing address. Gift recipients can have no email; the welcome letter goes by post.</span>
          <AddressFields a={r.address} set={(address) => set({ ...r, address })} />
        </>
      )}
    </div>
  )
}
